import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppLayout,
  ContentLayout,
  Header,
  Tabs,
  BreadcrumbGroup,
  Button,
  SpaceBetween,
  Alert,
} from '@amzn/awsui-components-react';
import { ActivitySidebar } from './sideNav';
import { publishKatalMetric } from '../katalAnalytics';
import { CoordinatorEventShiftsList } from './coordinatorEventShiftsList';
import {
  CREATE_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE,
  EVENT_CREATION,
  TRACK_UNLISTED_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE,
} from '../../utils/constants';
import { useAttendeeSessionStorage } from '../../hooks/attendance/attendeeSessionStorage';
import { useParticipantSessionStorage } from '../../hooks/attendance/participantSessionStorage';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { CoordinatorEventList } from './coordinatorEventList';
import { EventStatus } from '@amzn/red-velvet-api';

type TabType = 'upcoming' | 'past' | 'draft';

export function CoordinatorPage() {
  const { t } = useTranslation(['activity', 'translation']);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const features = useFeatures();
  const { resetAttendees } = useAttendeeSessionStorage();
  const { resetParticipants } = useParticipantSessionStorage();

  const urlFeatures = searchParams.get('features');
  let coordinatorActivityUrl = `/coordinator-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;

  const tabParam = searchParams.get('tab') as TabType | null;
  const activeTabId: TabType =
    tabParam && ['upcoming', 'past'].includes(tabParam) ? tabParam : 'upcoming';

  const now = useRef(new Date()).current;

  if (featureIsEnabled(EVENT_CREATION, features, searchParams) && tabParam) {
    coordinatorActivityUrl += urlFeatures ? `&tab=${tabParam}` : `?tab=${tabParam}`;
  }

  const getTabView = () => {
    switch (tabParam) {
      case 'past':
        return (
          <CoordinatorEventShiftsList
            endDate={now}
            sort="newest-first"
            isPastTab={true}
            status={EventStatus.PUBLISHED}
          />
        );
      case 'draft':
        return (
          <SpaceBetween size="s">
            <Alert type="warning">{t('draftInfoAlert')}</Alert>
            <CoordinatorEventList sort="newest-first" status={EventStatus.DRAFT} />
          </SpaceBetween>
        );
      case 'upcoming':
      default:
        return (
          <CoordinatorEventShiftsList
            startDate={now}
            sort="oldest-first"
            isPastTab={false}
            status={EventStatus.PUBLISHED}
          />
        );
    }
  };

  const getContentV1 = () => {
    return (
      <div className="page-content">
        <ContentLayout
          header={
            <div className="page-content-header">
              <Header
                variant="h1"
                actions={
                  <Button
                    variant="primary"
                    href={`/track-attendance${urlFeatures ? '?features=' + urlFeatures : ''}`}
                    data-aci-analytics-name={TRACK_UNLISTED_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE}
                    onFollow={() => {
                      // A safeguard against these not being properly removed when navigating away from an in progress attendance tracking
                      resetAttendees();
                      resetParticipants();
                    }}
                  >
                    {t('trackUnlistedEvent')}
                  </Button>
                }
              >
                {t('pageTitle.coordinatorActivity')}
              </Header>
            </div>
          }
        >
          <Tabs
            activeTabId={activeTabId}
            onChange={({ detail }) => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('tab', detail.activeTabId);
              setSearchParams(newParams);
            }}
            tabs={[
              {
                label: t('upcoming'),
                id: 'upcoming',
                content: (
                  <CoordinatorEventShiftsList
                    startDate={now}
                    sort="oldest-first"
                    isPastTab={false}
                  />
                ),
              },
              {
                label: t('past'),
                id: 'past',
                content: (
                  <CoordinatorEventShiftsList endDate={now} sort="newest-first" isPastTab={true} />
                ),
              },
            ]}
          />
        </ContentLayout>
      </div>
    );
  };

  const getHeader = () => {
    switch (tabParam) {
      case 'past':
        return t('past');
      case 'draft':
        return t('draft');
      case 'upcoming':
      default:
        return t('upcoming');
    }
  };

  const getContentViewV2 = () => {
    return (
      <div className="page-content">
        <ContentLayout
          header={
            <div className="page-content-header">
              <Header
                variant="h1"
                actions={
                  <SpaceBetween size="s" direction="horizontal">
                    <Button
                      variant="normal"
                      href={`/track-attendance${urlFeatures ? '?features=' + urlFeatures : ''}`}
                      data-aci-analytics-name={TRACK_UNLISTED_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE}
                      onFollow={() => {
                        // A safeguard against these not being properly removed when navigating away from an in progress attendance tracking
                        resetAttendees();
                        resetParticipants();
                      }}
                    >
                      {t('trackUnlistedEvent')}
                    </Button>
                    <Button
                      variant="primary"
                      iconName="add-plus"
                      href={`/create-event${urlFeatures ? '?features=' + urlFeatures : ''}`}
                      data-aci-analytics-name={CREATE_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE}
                      onFollow={() => {
                        publishKatalMetric('createEvent_entryPoint', 'coordinatorActivity');
                        // A safeguard against these not being properly removed when navigating away from an in progress attendance tracking
                        resetAttendees();
                        resetParticipants();
                      }}
                    >
                      {t('createEvent')}
                    </Button>
                  </SpaceBetween>
                }
              >
                {getHeader()}
              </Header>
            </div>
          }
        >
          {getTabView()}
        </ContentLayout>
      </div>
    );
  };

  const getContent = () => {
    if (featureIsEnabled(EVENT_CREATION, features, searchParams)) {
      return getContentViewV2();
    }
    return getContentV1();
  };

  return (
    <AppLayout
      footerSelector=".footer"
      navigationHide={false}
      navigation={<ActivitySidebar baseUrl={coordinatorActivityUrl} />}
      toolsHide={true}
      breadcrumbs={
        <div className="breadcrumbs">
          <BreadcrumbGroup
            items={[
              { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
              { text: t('breadcrumbs.coordinatorActivity'), href: '#' },
            ]}
            onFollow={(event) => {
              navigate(event.detail.href);
              event.preventDefault();
            }}
          />
        </div>
      }
      content={getContent()}
      contentType="cards"
    />
  );
}
